import { ApolloError, useApolloClient } from "@apollo/client";
import type { CheckInSlipData } from "@ths/shared/printing";
import { formatDODID, isValidDODID } from "@ths/shared/utils";
import { useCallback, useEffect, useRef, useState } from "react";
import type { FC, FormEvent } from "react";

import { resetAnalytics, trackBasecampEvent } from "../analytics";
import {
  SelfCheckinLookupNotFoundError,
  presentSelfCheckinError,
  presentSelfCheckinLookupError,
} from "../api/selfCheckinError";
import GoodbyeScreen from "../components/GoodbyeScreen";
import KioskCheckInSlipScreen from "../components/KioskCheckInSlipScreen";
import KioskIdleScreen from "../components/KioskIdleScreen";
import type { CheckInContact } from "../components/ReminderConsentFields";
import Shell from "../components/Shell";
import { useCheckoutReminderOptionsQuery } from "../generated/graphql";
import {
  SelfCheckinDocument,
  useEndSelfCheckinSessionMutation,
  useGetSelfCheckOutChecklistLazyQuery,
  useGetSelfCheckinLookupLazyQuery,
  useSelfCheckOutMutation,
  type GetSelfCheckOutChecklistQuery,
  type SelfCheckinIdentityInput,
  SelfCheckinIdentityType,
  SelfCheckinLifecycleSource,
  type SelfCheckinLookupReservation,
  type SelfCheckinMutation,
  type SelfCheckinMutationVariables,
} from "../generated/graphql";
import BasecampSelectStep from "../steps/BasecampSelectStep";
import CivilianLookupStep, {
  type CivilianLookupValues,
} from "../steps/CivilianLookupStep";
import ConfirmStep from "../steps/ConfirmStep";
import EdipiStep from "../steps/EdipiStep";
import IdentityChoiceStep from "../steps/IdentityChoiceStep";
import InventoryStep from "../steps/InventoryStep";
import VehicleStep, { type ArrivalInput } from "../steps/VehicleStep";

type CheckinData = SelfCheckinMutation["selfCheckin"];
type ChecklistItem =
  GetSelfCheckOutChecklistQuery["selfCheckOutChecklist"][number];
type PendingContact = CheckInContact;

type KioskStep =
  | "idle"
  | "identity-choice"
  | "checkout-identity-choice"
  | "edipi-checkin"
  | "civilian-checkin"
  | "edipi-checkout"
  | "civilian-checkout"
  | "basecampSelect"
  | "confirm"
  | "vehicle"
  | "checkInSlip"
  | "inventory"
  | "goodbye";

const SLIP_COUNTDOWN_SECONDS = 20;
const IDLE_RESET_MS = 20_000;

const buildDodIdentity = (edipi: string): SelfCheckinIdentityInput => ({
  type: SelfCheckinIdentityType.DodId,
  edipi: formatDODID(edipi),
});

const buildCivilianIdentity = (
  values: CivilianLookupValues,
): SelfCheckinIdentityInput => ({
  type: SelfCheckinIdentityType.CivilianNameDate,
  firstName: values.firstName,
  lastName: values.lastName,
  checkInDate: values.checkInDate,
  checkOutDate: values.checkOutDate,
});

const KioskPage: FC = () => {
  const [step, setStep] = useState<KioskStep>("idle");
  const [edipi, setEdipi] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [checkinIdentity, setCheckinIdentity] =
    useState<SelfCheckinIdentityInput | null>(null);
  const [civilianLookupStatus, setCivilianLookupStatus] = useState<
    "entry" | "not-found"
  >("entry");
  const [selectReservations, setSelectReservations] = useState<
    SelfCheckinLookupReservation[]
  >([]);
  const [selectedReservation, setSelectedReservation] =
    useState<SelfCheckinLookupReservation | null>(null);
  const reminderOptions = useCheckoutReminderOptionsQuery();
  const [pendingContact, setPendingContact] = useState<PendingContact | null>(
    null,
  );
  const [checkinResult, setCheckinResult] = useState<CheckinData | null>(null);
  const [checkoutToken, setCheckoutToken] = useState<string | null>(null);
  const [checkoutRoomNumber, setCheckoutRoomNumber] = useState<string>("");
  const [inventoryItems, setInventoryItems] = useState<ChecklistItem[]>([]);
  const [goodbyeName, setGoodbyeName] = useState("");
  const [selectionFlow, setSelectionFlow] = useState<"checkin" | "checkout">(
    "checkin",
  );

  const apolloClient = useApolloClient();
  const [doLookup] = useGetSelfCheckinLookupLazyQuery({
    fetchPolicy: "network-only",
  });
  const [doFetchChecklist] = useGetSelfCheckOutChecklistLazyQuery({
    fetchPolicy: "network-only",
  });
  const [doCheckOut] = useSelfCheckOutMutation();
  const [endSession] = useEndSelfCheckinSessionMutation();
  const doCheckIn = useCallback(
    (variables: SelfCheckinMutationVariables) =>
      apolloClient.mutate<SelfCheckinMutation, SelfCheckinMutationVariables>({
        mutation: SelfCheckinDocument,
        variables,
        errorPolicy: "all",
        fetchPolicy: "no-cache",
      }),
    [apolloClient],
  );

  // Generation fencing makes every async result belong to one kiosk guest
  // session. Ref-based single-flight protection is synchronous, so a rapid
  // second tap cannot enter before React has rerendered `loading`.
  const sessionGenerationRef = useRef(0);
  const selfCheckinAttemptRef = useRef<number | null>(null);
  const invalidateSession = useCallback(() => {
    sessionGenerationRef.current += 1;
    selfCheckinAttemptRef.current = null;
  }, []);
  const isCurrentSession = useCallback(
    (generation: number) => sessionGenerationRef.current === generation,
    [],
  );
  const beginSelfCheckinAttempt = useCallback(
    (generation: number): number | null => {
      if (
        !isCurrentSession(generation) ||
        selfCheckinAttemptRef.current !== null
      ) {
        return null;
      }
      selfCheckinAttemptRef.current = generation;
      return generation;
    },
    [isCurrentSession],
  );
  const finishSelfCheckinAttempt = useCallback((generation: number) => {
    if (selfCheckinAttemptRef.current === generation) {
      selfCheckinAttemptRef.current = null;
    }
  }, []);

  useEffect(
    () => () => {
      invalidateSession();
    },
    [invalidateSession],
  );

  const resetToIdle = useCallback(
    (
      resetReason: "cancel" | "complete" | "idle_timeout" | "manual" = "manual",
    ) => {
      const token = checkoutToken ?? checkinResult?.token;
      if (token) {
        void endSession({ variables: { token } }).catch(() => undefined);
      }
      invalidateSession();
      if (step !== "idle") {
        trackBasecampEvent("basecamp_kiosk_reset", {
          flow: "kiosk",
          resetReason,
          step,
          surface: "kiosk",
        });
        resetAnalytics();
      }

      setStep("idle");
      setEdipi("");
      setError("");
      setLoading(false);
      setCheckinIdentity(null);
      setCivilianLookupStatus("entry");
      setSelectReservations([]);
      setSelectedReservation(null);
      setPendingContact(null);
      setCheckinResult(null);
      setCheckoutToken(null);
      setCheckoutRoomNumber("");
      setInventoryItems([]);
      setGoodbyeName("");
      setSelectionFlow("checkin");
      // Wipe the in-memory Apollo cache so the previous guest's check-in
      // lookup data is not retained between sessions. Self-check-in itself is
      // executed with client.mutate + no-cache, so its token and codes never
      // enter hook result state or the normalized cache.
      void apolloClient.clearStore();
    },
    [
      apolloClient,
      checkinResult?.token,
      checkoutToken,
      endSession,
      invalidateSession,
      step,
    ],
  );

  const lastTrackedStepRef = useRef<string | null>(null);
  useEffect(() => {
    const isCheckoutStep =
      step === "checkout-identity-choice" ||
      step === "civilian-checkout" ||
      step === "edipi-checkout" ||
      step === "inventory" ||
      (step === "goodbye" && checkoutToken);
    const flow =
      step === "idle" ? "kiosk" : isCheckoutStep ? "checkout" : "checkin";
    const trackedStepKey = `${flow}:${step}`;
    if (lastTrackedStepRef.current === trackedStepKey) return;
    lastTrackedStepRef.current = trackedStepKey;

    trackBasecampEvent("basecamp_flow_step_viewed", {
      flow,
      step,
      surface: "kiosk",
    });
  }, [checkoutToken, step]);

  const startCheckoutForReservation = useCallback(
    async (
      identity: SelfCheckinIdentityInput,
      reservation: SelfCheckinLookupReservation,
      generation: number,
    ) => {
      const attempt = beginSelfCheckinAttempt(generation);
      if (attempt === null) return;

      try {
        setSelectedReservation(reservation);
        const { data: ciData, errors: ciErrors } = await doCheckIn({
          input: {
            identity,
            assignmentId: reservation.assignmentId,
            source: SelfCheckinLifecycleSource.Kiosk,
            ...(reservation.guestPhone
              ? { phone: reservation.guestPhone }
              : {}),
          },
        });
        if (!isCurrentSession(attempt)) return;
        if (ciErrors?.length || !ciData?.selfCheckin) {
          throw new ApolloError({ graphQLErrors: ciErrors });
        }
        const token = ciData.selfCheckin.token;
        setCheckoutToken(token);
        setCheckoutRoomNumber(ciData.selfCheckin.roomNumber);
        setGoodbyeName(
          `${ciData.selfCheckin.guestFirstName} ${ciData.selfCheckin.guestLastName}`,
        );

        const { data: clData, error: clError } = await doFetchChecklist({
          variables: {
            token,
            assignmentId: reservation.assignmentId,
          },
        });
        if (!isCurrentSession(attempt)) return;
        if (clError) throw clError;
        const items = clData?.selfCheckOutChecklist ?? [];
        trackBasecampEvent("basecamp_checkout_started", {
          flow: "checkout",
          hasChecklist: items.length > 0,
          surface: "kiosk",
        });
        if (items.length === 0) {
          const { errors: coErrors } = await doCheckOut({
            variables: {
              token,
              assignmentId: reservation.assignmentId,
              missingItemIds: null,
              source: SelfCheckinLifecycleSource.Kiosk,
            },
          });
          if (!isCurrentSession(attempt)) return;
          if (coErrors?.length) {
            throw new ApolloError({ graphQLErrors: coErrors });
          }
          trackBasecampEvent("basecamp_checkout_completed", {
            flow: "checkout",
            missingItemCount: 0,
            surface: "kiosk",
          });
          setStep("goodbye");
          return;
        }
        setInventoryItems(items);
        setStep("inventory");
      } finally {
        finishSelfCheckinAttempt(attempt);
      }
    },
    [
      beginSelfCheckinAttempt,
      doCheckIn,
      doCheckOut,
      doFetchChecklist,
      finishSelfCheckinAttempt,
      isCurrentSession,
    ],
  );

  const handleEdipiSubmit = useCallback(
    async (e?: FormEvent) => {
      e?.preventDefault();
      if (!isValidDODID(edipi) || loading) return;
      const generation = sessionGenerationRef.current;
      setError("");
      setLoading(true);
      const flow = step === "edipi-checkout" ? "checkout" : "checkin";
      const identity = buildDodIdentity(edipi);
      setCheckinIdentity(identity);
      let lookupSucceeded = false;
      trackBasecampEvent("basecamp_lookup_submitted", {
        flow,
        surface: "kiosk",
      });
      try {
        if (step === "edipi-checkout") {
          // We need a session token to fetch the checklist. selfCheckin is
          // idempotent for an already-checked-in guest, and the lookup gives us
          // the assignment id required to get that token.
          const { data: lookupData, error: lookupError } = await doLookup({
            variables: { identity },
          });
          if (!isCurrentSession(generation)) return;
          if (lookupError) throw lookupError;
          if (!lookupData?.selfCheckinLookup) {
            throw new Error("Missing lookup data");
          }
          const checkoutReservations =
            lookupData.selfCheckinLookup.reservations.filter((reservation) =>
              Boolean(reservation.checkedInAt),
            );
          if (checkoutReservations.length === 0) {
            throw new SelfCheckinLookupNotFoundError("checkout");
          }
          lookupSucceeded = true;
          trackBasecampEvent("basecamp_lookup_succeeded", {
            basecampId: checkoutReservations[0].basecampId,
            flow,
            hasMultipleReservations: checkoutReservations.length > 1,
            reservationCount: checkoutReservations.length,
            surface: "kiosk",
          });
          if (checkoutReservations.length === 1) {
            await startCheckoutForReservation(
              identity,
              checkoutReservations[0],
              generation,
            );
          } else {
            setSelectionFlow("checkout");
            setSelectReservations(checkoutReservations);
            setStep("basecampSelect");
          }
          return;
        }

        // Default: check-in path
        const { data, error: gqlError } = await doLookup({
          variables: { identity },
        });
        if (!isCurrentSession(generation)) return;
        if (gqlError) throw gqlError;
        if (!data?.selfCheckinLookup) throw new Error("Missing lookup data");
        const reservations = data.selfCheckinLookup.reservations;
        if (reservations.length === 0) {
          throw new SelfCheckinLookupNotFoundError("checkin");
        }
        lookupSucceeded = true;
        trackBasecampEvent("basecamp_lookup_succeeded", {
          basecampId:
            reservations.length === 1 ? reservations[0].basecampId : undefined,
          flow,
          hasMultipleReservations: reservations.length > 1,
          reservationCount: reservations.length,
          surface: "kiosk",
        });
        if (reservations.length === 1) {
          setSelectedReservation(reservations[0]);
          setStep("confirm");
        } else {
          setSelectionFlow("checkin");
          setSelectReservations(reservations);
          setStep("basecampSelect");
        }
      } catch (err) {
        if (!isCurrentSession(generation)) return;
        if (flow === "checkout" && lookupSucceeded) {
          trackBasecampEvent("basecamp_checkout_failed", {
            errorCategory: "checkout_failed",
            flow,
            missingItemCount: 0,
            surface: "kiosk",
          });
        } else {
          trackBasecampEvent("basecamp_lookup_failed", {
            errorCategory: "lookup_failed",
            flow,
            surface: "kiosk",
          });
        }
        setError(
          flow === "checkout" && lookupSucceeded
            ? presentSelfCheckinError(err, "checkout").message
            : presentSelfCheckinLookupError(err, flow).message,
        );
      } finally {
        if (isCurrentSession(generation)) setLoading(false);
      }
    },
    [
      doLookup,
      edipi,
      isCurrentSession,
      loading,
      startCheckoutForReservation,
      step,
    ],
  );

  const handleCivilianLookup = useCallback(
    async (values: CivilianLookupValues) => {
      if (loading) return;
      const generation = sessionGenerationRef.current;
      const identity = buildCivilianIdentity(values);
      const flow = step === "civilian-checkout" ? "checkout" : "checkin";
      setError("");
      setLoading(true);
      setCivilianLookupStatus("entry");
      setCheckinIdentity(identity);
      let lookupSucceeded = false;
      trackBasecampEvent("basecamp_lookup_submitted", {
        flow,
        surface: "kiosk",
      });
      try {
        const { data, error: gqlError } = await doLookup({
          variables: { identity },
        });
        if (!isCurrentSession(generation)) return;
        if (gqlError) throw gqlError;
        if (!data?.selfCheckinLookup) throw new Error("Missing lookup data");
        const lookupReservations = data.selfCheckinLookup.reservations;
        const reservations =
          flow === "checkout"
            ? lookupReservations.filter((reservation) =>
                Boolean(reservation.checkedInAt),
              )
            : lookupReservations;
        if (reservations.length !== 1) {
          trackBasecampEvent("basecamp_lookup_failed", {
            errorCategory: "lookup_failed",
            flow,
            surface: "kiosk",
          });
          setCivilianLookupStatus("not-found");
          return;
        }
        lookupSucceeded = true;
        trackBasecampEvent("basecamp_lookup_succeeded", {
          basecampId: reservations[0].basecampId,
          flow,
          hasMultipleReservations: false,
          reservationCount: 1,
          surface: "kiosk",
        });
        if (flow === "checkout") {
          await startCheckoutForReservation(
            identity,
            reservations[0],
            generation,
          );
          return;
        }
        setSelectedReservation(reservations[0]);
        setStep("confirm");
      } catch (err) {
        if (!isCurrentSession(generation)) return;
        if (flow === "checkout" && lookupSucceeded) {
          trackBasecampEvent("basecamp_checkout_failed", {
            errorCategory: "checkout_failed",
            flow,
            missingItemCount: 0,
            surface: "kiosk",
          });
        } else {
          trackBasecampEvent("basecamp_lookup_failed", {
            errorCategory: "lookup_failed",
            flow,
            surface: "kiosk",
          });
        }
        setError(
          flow === "checkout" && lookupSucceeded
            ? presentSelfCheckinError(err, "checkout").message
            : presentSelfCheckinLookupError(err, flow).message,
        );
      } finally {
        if (isCurrentSession(generation)) setLoading(false);
      }
    },
    [doLookup, isCurrentSession, loading, startCheckoutForReservation, step],
  );

  const handleBasecampSelect = useCallback(
    (reservation: SelfCheckinLookupReservation) => {
      setSelectedReservation(reservation);
      if (selectionFlow === "checkout" && checkinIdentity) {
        const generation = sessionGenerationRef.current;
        setError("");
        setLoading(true);
        void startCheckoutForReservation(
          checkinIdentity,
          reservation,
          generation,
        )
          .catch((err) => {
            if (!isCurrentSession(generation)) return;
            setError(presentSelfCheckinError(err, "checkout").message);
          })
          .finally(() => {
            if (isCurrentSession(generation)) setLoading(false);
          });
        return;
      }
      setStep("confirm");
    },
    [
      checkinIdentity,
      isCurrentSession,
      selectionFlow,
      startCheckoutForReservation,
    ],
  );

  const handleConfirm = useCallback((contact: PendingContact) => {
    setPendingContact(contact);
    setStep("vehicle");
  }, []);

  const handleVehicleSubmit = useCallback(
    async (vehicle: ArrivalInput) => {
      const generation = sessionGenerationRef.current;
      const attempt = beginSelfCheckinAttempt(generation);
      if (attempt === null) return;

      setError("");
      setLoading(true);
      try {
        const { data, errors } = await doCheckIn({
          input: {
            identity: checkinIdentity ?? buildDodIdentity(edipi),
            assignmentId: selectedReservation!.assignmentId,
            source: SelfCheckinLifecycleSource.Kiosk,
            ...(vehicle.phone ? { phone: vehicle.phone } : {}),
            ...(pendingContact?.email ? { email: pendingContact.email } : {}),
            ...(vehicle.smsPromptDismissed ? { smsPromptDismissed: true } : {}),
            ...(vehicle.smsConsent
              ? {
                  phone: vehicle.phone,
                  smsConsent: true,
                  smsDisclosureVersion: vehicle.smsDisclosureVersion,
                }
              : {}),
            ...(vehicle.carPlateNumber
              ? { carPlateNumber: vehicle.carPlateNumber }
              : {}),
            ...(vehicle.carMake ? { carMake: vehicle.carMake } : {}),
          },
        });
        if (!isCurrentSession(attempt)) return;
        if (errors?.length || !data?.selfCheckin) {
          throw new ApolloError({ graphQLErrors: errors });
        }
        trackBasecampEvent("basecamp_checkin_completed", {
          basecampId: selectedReservation?.basecampId,
          emailProvided: Boolean(pendingContact?.email),
          flow: "checkin",
          hasVehicle: Boolean(vehicle.carPlateNumber || vehicle.carMake),
          surface: "kiosk",
        });
        // Note: NEVER call storeToken — kiosk does not persist.
        setCheckinResult(data.selfCheckin);
        setStep("checkInSlip");
      } catch (err) {
        if (!isCurrentSession(attempt)) return;
        const failure = presentSelfCheckinError(err);
        trackBasecampEvent("basecamp_checkin_failed", {
          basecampId: selectedReservation?.basecampId,
          emailProvided: Boolean(pendingContact?.email),
          errorCategory: "checkin_failed",
          flow: "checkin",
          hasVehicle: Boolean(vehicle.carPlateNumber || vehicle.carMake),
          surface: "kiosk",
        });
        setError(failure.message);
        if (failure.action === "edit_email") setStep("confirm");
      } finally {
        finishSelfCheckinAttempt(attempt);
        if (isCurrentSession(attempt)) setLoading(false);
      }
    },
    [
      beginSelfCheckinAttempt,
      checkinIdentity,
      doCheckIn,
      edipi,
      finishSelfCheckinAttempt,
      isCurrentSession,
      pendingContact,
      selectedReservation,
    ],
  );

  const handleVehicleBack = useCallback(() => {
    invalidateSession();
    setError("");
    setLoading(false);
    setStep("confirm");
  }, [invalidateSession]);

  const handleInventorySubmit = useCallback(
    async (missingItemIds: number[]) => {
      if (!checkoutToken || !selectedReservation) return;
      const generation = sessionGenerationRef.current;
      const attempt = beginSelfCheckinAttempt(generation);
      if (attempt === null) return;

      setError("");
      setLoading(true);
      trackBasecampEvent("basecamp_inventory_submitted", {
        flow: "checkout",
        missingItemCount: missingItemIds.length,
        surface: "kiosk",
      });
      try {
        const { errors } = await doCheckOut({
          variables: {
            token: checkoutToken,
            assignmentId: selectedReservation.assignmentId,
            missingItemIds: missingItemIds.length > 0 ? missingItemIds : null,
            source: SelfCheckinLifecycleSource.Kiosk,
          },
        });
        if (!isCurrentSession(attempt)) return;
        if (errors?.length) {
          throw new ApolloError({ graphQLErrors: errors });
        }
        trackBasecampEvent("basecamp_checkout_completed", {
          flow: "checkout",
          missingItemCount: missingItemIds.length,
          surface: "kiosk",
        });
        setStep("goodbye");
      } catch (err) {
        if (!isCurrentSession(attempt)) return;
        trackBasecampEvent("basecamp_checkout_failed", {
          errorCategory: "checkout_failed",
          flow: "checkout",
          missingItemCount: missingItemIds.length,
          surface: "kiosk",
        });
        setError(presentSelfCheckinError(err, "checkout").message);
      } finally {
        finishSelfCheckinAttempt(attempt);
        if (isCurrentSession(attempt)) setLoading(false);
      }
    },
    [
      beginSelfCheckinAttempt,
      checkoutToken,
      doCheckOut,
      finishSelfCheckinAttempt,
      isCurrentSession,
      selectedReservation,
    ],
  );

  const handleInventoryCancel = useCallback(() => {
    resetToIdle("cancel");
  }, [resetToIdle]);

  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    // Only run the in-flow idle timer on partial-input states.
    // - inventory is excluded: guests may walk away from the kiosk during the
    //   room walkthrough and shouldn't lose progress.
    // - checkInSlip/goodbye have their own countdowns.
    // - idle has no state to reset.
    const shouldArm =
      step === "edipi-checkin" ||
      step === "edipi-checkout" ||
      step === "identity-choice" ||
      step === "checkout-identity-choice" ||
      step === "civilian-checkin" ||
      step === "civilian-checkout" ||
      step === "basecampSelect" ||
      step === "confirm" ||
      step === "vehicle";

    if (!shouldArm) {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
      return;
    }

    const arm = () => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      idleTimerRef.current = setTimeout(
        () => resetToIdle("idle_timeout"),
        IDLE_RESET_MS,
      );
    };

    arm();
    const events: Array<keyof WindowEventMap> = [
      "keydown",
      "pointerdown",
      "touchstart",
    ];
    events.forEach((evt) => window.addEventListener(evt, arm));
    return () => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      events.forEach((evt) => window.removeEventListener(evt, arm));
    };
  }, [step, resetToIdle]);

  if (step === "idle") {
    return (
      <Shell>
        <KioskIdleScreen
          onCheckIn={() => {
            resetToIdle();
            setStep("identity-choice");
          }}
          onCheckOut={() => {
            resetToIdle();
            setStep("checkout-identity-choice");
          }}
        />
      </Shell>
    );
  }

  if (step === "identity-choice") {
    return (
      <Shell>
        <IdentityChoiceStep
          flow="checkin"
          onSoldier={() => {
            setError("");
            setCivilianLookupStatus("entry");
            setStep("edipi-checkin");
          }}
          onCivilian={() => {
            setError("");
            setEdipi("");
            setCheckinIdentity(null);
            setCivilianLookupStatus("entry");
            setStep("civilian-checkin");
          }}
          onBack={() => resetToIdle("cancel")}
        />
      </Shell>
    );
  }

  if (step === "checkout-identity-choice") {
    return (
      <Shell>
        <IdentityChoiceStep
          flow="checkout"
          onSoldier={() => {
            setError("");
            setCivilianLookupStatus("entry");
            setStep("edipi-checkout");
          }}
          onCivilian={() => {
            setError("");
            setEdipi("");
            setCheckinIdentity(null);
            setCivilianLookupStatus("entry");
            setStep("civilian-checkout");
          }}
          onBack={() => resetToIdle("cancel")}
        />
      </Shell>
    );
  }

  if (step === "civilian-checkin" || step === "civilian-checkout") {
    return (
      <Shell>
        <CivilianLookupStep
          onSubmit={handleCivilianLookup}
          isSubmitting={loading}
          error={error}
          status={civilianLookupStatus}
          flow={step === "civilian-checkout" ? "checkout" : "checkin"}
          onRetry={() => {
            setError("");
            setCivilianLookupStatus("entry");
          }}
          onBack={() => {
            invalidateSession();
            setError("");
            setLoading(false);
            setCheckinIdentity(null);
            setCivilianLookupStatus("entry");
            setStep(
              step === "civilian-checkout"
                ? "checkout-identity-choice"
                : "identity-choice",
            );
          }}
        />
      </Shell>
    );
  }

  if (step === "edipi-checkin" || step === "edipi-checkout") {
    return (
      <Shell>
        <EdipiStep
          edipi={edipi}
          setEdipi={setEdipi}
          onSubmit={handleEdipiSubmit}
          isSubmitting={loading}
          error={error}
          mode={step === "edipi-checkout" ? "login" : "checkin"}
          onBack={
            step === "edipi-checkin"
              ? () => {
                  invalidateSession();
                  setError("");
                  setLoading(false);
                  setEdipi("");
                  setCheckinIdentity(null);
                  setCivilianLookupStatus("entry");
                  setStep("identity-choice");
                }
              : () => {
                  invalidateSession();
                  setError("");
                  setLoading(false);
                  setEdipi("");
                  setCheckinIdentity(null);
                  setCivilianLookupStatus("entry");
                  setStep("checkout-identity-choice");
                }
          }
        />
      </Shell>
    );
  }

  if (step === "basecampSelect" && selectReservations.length > 0) {
    return (
      <Shell>
        <main className="relative z-10 flex flex-1 flex-col items-center justify-center px-6 pb-12 pt-10">
          <BasecampSelectStep
            reservations={selectReservations}
            flow={selectionFlow}
            onSelect={handleBasecampSelect}
            onCancel={() => {
              invalidateSession();
              setSelectReservations([]);
              setError("");
              setLoading(false);
              setStep(
                selectionFlow === "checkout"
                  ? "edipi-checkout"
                  : "edipi-checkin",
              );
            }}
          />
        </main>
      </Shell>
    );
  }

  if (step === "confirm" && selectedReservation) {
    return (
      <Shell>
        <main className="relative z-10 flex flex-1 flex-col items-center justify-center px-6 pb-12 pt-10">
          <ConfirmStep
            lookup={selectedReservation}
            isSubmitting={loading}
            error={error}
            initialEmail={pendingContact?.email ?? ""}
            onConfirm={handleConfirm}
            onBack={resetToIdle}
          />
        </main>
      </Shell>
    );
  }

  if (step === "vehicle") {
    return (
      <Shell>
        <main className="relative z-10 flex flex-1 flex-col items-center justify-center px-6 pb-12 pt-10">
          <VehicleStep
            key={selectedReservation?.assignmentId}
            stay={selectedReservation ?? undefined}
            collectReminders
            initialSmsReminderStatus={selectedReservation?.smsReminderStatus}
            reminderOptions={reminderOptions.data?.checkoutReminderOptions}
            reminderOptionsLoading={reminderOptions.loading}
            onRetryReminderOptions={() => {
              void reminderOptions.refetch();
            }}
            isSubmitting={loading}
            error={error}
            initialPhone={
              pendingContact?.phone ?? selectedReservation?.guestPhone ?? ""
            }
            onSubmit={handleVehicleSubmit}
            onBack={handleVehicleBack}
          />
        </main>
      </Shell>
    );
  }

  if (step === "checkInSlip" && checkinResult) {
    const slipData: CheckInSlipData = {
      roomNumber: checkinResult.roomNumber,
      buildingName: checkinResult.buildingName ?? null,
      buildingAccessCode: checkinResult.buildingAccessCode ?? null,
      roomAccessCode: checkinResult.roomAccessCode ?? null,
    };
    return (
      <Shell>
        <KioskCheckInSlipScreen
          data={slipData}
          countdownSeconds={SLIP_COUNTDOWN_SECONDS}
          onComplete={() => resetToIdle("complete")}
        />
      </Shell>
    );
  }

  if (step === "inventory") {
    return (
      <Shell>
        <InventoryStep
          roomNumber={checkoutRoomNumber}
          items={inventoryItems}
          isSubmitting={loading}
          error={error}
          onCancel={handleInventoryCancel}
          onSubmit={handleInventorySubmit}
        />
      </Shell>
    );
  }

  if (step === "goodbye") {
    return (
      <GoodbyeScreen
        guestName={goodbyeName}
        onComplete={() => resetToIdle("complete")}
      />
    );
  }

  return null;
};

export default KioskPage;
